import { setLocale } from 'yup'
import { ru } from 'yup-locales'
import { PageLink, PageTitle } from '../../../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { Table } from '../../../../../../../components/Table'
import { TableHead } from '../../../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../../../components/Table/TableBody'
import { useCallback, useEffect, useMemo, useState } from 'react'
import MessageTemplateItem from './MessageTemplateItem'
import { LoaderForTable } from '../../../../../../../components/Loader'
import { IMessageSender, IMessageSenderLookup, IMessageTemplate, IRequisiteType } from '../../core/response.types'
import { getMessageSenderList, getMessageSenderLookupList, getMessageTemplateList } from '../../core/api'
import { MessageTemplateListQuery } from '../../core/query.types'
import { useLocation } from 'react-router-dom'
import { MESSAGE_BREADCRUMBS } from '../../core/consts'
import { DEFAULT_MESSAGE_SENDER_QUERY, DEFAULT_MESSAGE_TEMPLATE_QUERY } from '../../core/query.defaults'
import { ApplyMessageTemplateFiltersArgs } from '../../core/types'
import { serializeFiltersToUrl } from '../../../../../../../utils/statusFiltersURL'
import ModalNewMessageTemplate from '../common/ModalNewMessageTemplate'
import ReactPaginate from 'react-paginate'

setLocale(ru)

export default () => {
    const location = useLocation()

    const breadCrumbs = useMemo<PageLink[]>(
        () => MESSAGE_BREADCRUMBS.map((b) => ({
            ...b,
            isActive: location.pathname === b.path,
        })),
        [location.pathname]
    )
    const [initialized, setInitialized] = useState(false)
    const [isInitializing, setInitializing] = useState(false)
    const [isLoading, setLoading] = useState(false)
    const [templates, setTemplates] = useState<IMessageTemplate[]>([])    
    const [templateCount, setTemplateCount] = useState(0)
    const [pageCount, setPageCount] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [query, setQuery] = useState<MessageTemplateListQuery>(DEFAULT_MESSAGE_TEMPLATE_QUERY)

    // ===== Справиочники =====
    const [lookupSenders, setLookupSenders] = useState<IMessageSenderLookup[]>([])

    const initializPage = async () => {
        setInitializing(true)

        try {
            const response = await getMessageSenderLookupList()
            if (!response?.data?.success) {
                throw new Error(
                    'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
                )
            }

            const list = response.data.data ?? []

            setLookupSenders(list)
        } catch (error) {
            console.error('Ошибка загрузки справочника:', error)
            setLookupSenders([])
        } finally {
            setInitializing(false)
        }
    }

    const loadMessageTemplateList = useCallback(
        async (params: MessageTemplateListQuery) => {
            setLoading(true)
            
            try {
                const response = await getMessageTemplateList(params)
                if (!response?.data?.success) {
                    throw new Error(
                        'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
                    )
                }

                const list = response.data.data || []
                const totalRows = response.data.totalRows || 0
                const pages = Math.ceil(totalRows / params.perPage)

                setTemplates(list)
                setTemplateCount(totalRows)
                setPageCount(pages)
            } catch (error) {
                console.error('Ошибка загрузки шаблонов:', error)
                setTemplates([])
                setTemplateCount(0)
                setPageCount(0)
            } finally {
                setLoading(false)
            }
        },
        []
    )

    const applyFilters = useCallback(
        ({ patch, page }: ApplyMessageTemplateFiltersArgs = {}) => {
            
            const nextQuery: MessageTemplateListQuery = {
                ...DEFAULT_MESSAGE_TEMPLATE_QUERY,
                ...query,
                ...patch,
                page: page ?? patch?.page ?? query.page,
            }

            setQuery(nextQuery)
    
            const urlQuery = serializeFiltersToUrl(nextQuery, DEFAULT_MESSAGE_TEMPLATE_QUERY)
            const nextUrl = urlQuery ? `${window.location.pathname}?${urlQuery}` : window.location.pathname
            window.history.replaceState({}, '', nextUrl)
    
            loadMessageTemplateList(nextQuery)
        },
        [query, loadMessageTemplateList]
    )

    const handleSaveSender = useCallback(
        (newSender: IMessageTemplate) => {   
            const incomingMatcher = newSender.matchers?.[0];
            if (!incomingMatcher) return;

            setTemplates((prev) => {
                const idx = prev.findIndex((t) => t.id === newSender.id);
                
                if (idx === -1) {
                    // TODO: Стоит отказаться от таког оподсчета в пользу templates.length или setTemplateCount заполнять через useEffect от templates.length
                    setTemplateCount((c) => c + 1);
                    return [...prev, newSender];
                }

                const existing = prev[idx];

                const updated = {
                    ...existing,
                    matchers: [...(existing.matchers ?? []), incomingMatcher],
                };

                return prev.map((t, i) => (i === idx ? updated : t));
            });                       
        },
        [query, loadMessageTemplateList]
    )

    useEffect(() => {
        if (initialized) return

        const init = async () => {
            await initializPage()
            applyFilters()
            setInitialized(true)
        }

        init()
    }, [initialized, applyFilters])

    useEffect(() => {
        if (!initialized) return

        if (location.hash === '#newTemplate') {
            setIsModalOpen(true)
        } else {
            setIsModalOpen(false)
        }
    }, [location.hash, initialized])

    return (
        <div>
            <div className='card sticky-top-tickets mb-7'>
                <PageTitle breadcrumbs={breadCrumbs}>Шаблоны</PageTitle>
                <div className='card-header border-0 pt-5 pb-2'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bold fs-3 mb-1'>Шаблоны сообщений</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">Всего шаблонов: {isLoading || isInitializing ? <span className='spinner-border spinner-border-sm align-middle'></span> : templateCount}</span>
                    </h3>
                    <div className="card-toolbar" style={{cursor: isLoading || isInitializing ? 'not-allowed' : 'pointer'}}>
                        <button 
                            className="btn btn-sm btn-primary"                            
                            disabled={isLoading || isInitializing}
                            onClick={() => setIsModalOpen(true)}
                        >
                            <KTIcon iconName="plus" className="fs-2" />
                            Добавить шаблон
                        </button>
                        {/* <CreateTemplateModalOneFile show={isShowAddTemplateModal} onHide={() => {}} /> */}
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-body py-3">
                    <Table>
                        <TableHead>
                            <tr>
                                <th className='min-w-40px max-w-40px align-middle text-center'></th>
                                <th className='min-w-150px'>Тип реквизита</th>
                                <th className='min-w-100px'>Способ доставки</th>
                                <th className='min-w-100px'>Тип операции</th>
                                <th className='min-w-100px'>Всего сообщений</th>
                                <th className='min-w-150px'>Дата последнего смс</th>
                            </tr>
                        </TableHead>
                        <TableBody>              
                            {!isLoading && !isInitializing && templates.length > 0 ? (
                                templates.map((template, key) => (
                                    <MessageTemplateItem
                                        key={template.id}
                                        template={template}
                                    />
                                ))
                            ) : (
                                <LoaderForTable isLoading={isLoading || isInitializing} colSpan={6} />
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
            {pageCount > 1 && (
                <div className='d-flex justify-content-center mt-5'>
                    <ReactPaginate
                        breakLabel='...'
                        nextLabel='Следующая >'
                        onPageChange={(selected) =>
                            applyFilters({ page: selected.selected + 1 })
                        }
                        pageRangeDisplayed={5}
                        pageCount={pageCount}
                        previousLabel='< Предыдущая'
                        renderOnZeroPageCount={() => null}
                        className='pagination'
                        activeClassName='active'
                        disabledClassName='disabled'
                        nextClassName='page-item'
                        nextLinkClassName='page-link'
                        pageClassName='page-item'
                        pageLinkClassName='page-link'
                        previousClassName='page-item'
                        previousLinkClassName='page-link'
                        breakClassName='page-item'
                        breakLinkClassName='page-link'
                    />
                </div>
            )}
            {isModalOpen && 
                <ModalNewMessageTemplate 
                    open={isModalOpen} 
                    onOpenChange={setIsModalOpen} 
                    lookupSenders={lookupSenders} 
                    onSave={handleSaveSender}
                />
            }
        </div>
    )
}
